import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtSDG } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Languages, Coffee, Plus, Minus, ShoppingBag, CheckCircle2, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function PublicMenu() {
    const { lang, setLang, t, dir } = useApp();
    const [data, setData] = useState(null);
    const [active, setActive] = useState(null);
    const [params] = useSearchParams();
    const tableNo = params.get("table");
    const [cart, setCart] = useState({});                    // {id: {item, qty}}
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [successOpen, setSuccessOpen] = useState(null);    // order_no on success
    const [orderForm, setOrderForm] = useState({
        customer_name: "",
        customer_phone: "",
        order_type: tableNo ? "dine_in" : "pickup",
        table_number: tableNo ? Number(tableNo) : null,
        delivery_address: "",
        payment_method: "",
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/menu/public").then((r) => {
            setData(r.data);
            if (r.data.categories.length) setActive(r.data.categories[0].id);
            // If current order_type was disabled, switch to first enabled
            const s = r.data.settings || {};
            const enabled = {
                dine_in: s.dine_in_enabled !== false,
                pickup: s.pickup_enabled !== false,
                delivery: s.delivery_enabled !== false,
            };
            setOrderForm((prev) => {
                if (enabled[prev.order_type]) return prev;
                const next = ["dine_in", "pickup", "delivery"].find((k) => enabled[k]);
                return next ? { ...prev, order_type: next } : prev;
            });
        }).catch(() => {});
    }, []);

    const orderTypes = useMemo(() => {
        const s = data?.settings || {};
        return [
            { key: "dine_in", enabled: s.dine_in_enabled !== false },
            { key: "pickup", enabled: s.pickup_enabled !== false },
            { key: "delivery", enabled: s.delivery_enabled !== false },
        ].filter((o) => o.enabled);
    }, [data]);

    const itemsByCat = useMemo(() => {
        if (!data) return [];
        return data.items.filter((i) => i.category_id === active);
    }, [data, active]);

    const cartTotals = useMemo(() => {
        const arr = Object.values(cart);
        const subtotal = arr.reduce((a, x) => a + x.item.price * x.qty, 0);
        const count = arr.reduce((a, x) => a + x.qty, 0);
        return { subtotal, count };
    }, [cart]);

    const add = (it) => {
        setCart((c) => {
            const x = c[it.id] || { item: it, qty: 0 };
            return { ...c, [it.id]: { ...x, qty: x.qty + 1 } };
        });
    };
    const sub = (id) => {
        setCart((c) => {
            const x = c[id];
            if (!x) return c;
            if (x.qty <= 1) { const { [id]: _, ...rest } = c; return rest; }
            return { ...c, [id]: { ...x, qty: x.qty - 1 } };
        });
    };
    const clearItem = (id) => {
        setCart((c) => { const { [id]: _, ...rest } = c; return rest; });
    };

    const submitOrder = async () => {
        const isDineIn = orderForm.order_type === "dine_in";
        // For dine-in: name & phone not required (the table is the identifier)
        if (!isDineIn && !orderForm.customer_name.trim()) { toast.error(t("customer_name")); return; }
        if (!isDineIn && !orderForm.customer_phone.trim()) { toast.error(t("customer_phone")); return; }
        if (isDineIn && (!orderForm.table_number || orderForm.table_number < 1 || orderForm.table_number > 70)) {
            toast.error(t("table_number")); return;
        }
        if (orderForm.order_type === "delivery" && (!orderForm.delivery_address || orderForm.delivery_address.trim().length < 5)) {
            toast.error(t("delivery_address")); return;
        }
        if (orderForm.order_type === "delivery" && !orderForm.payment_method) {
            toast.error(t("payment_method")); return;
        }
        setSubmitting(true);
        try {
            const body = {
                items: Object.values(cart).map((x) => ({
                    menu_item_id: x.item.id,
                    name_en: x.item.name_en,
                    name_ar: x.item.name_ar,
                    price: x.item.price,
                    qty: x.qty,
                    notes: "",
                })),
                customer_name: isDineIn
                    ? (lang === "ar" ? `طاولة ${orderForm.table_number}` : `Table ${orderForm.table_number}`)
                    : orderForm.customer_name.trim(),
                customer_phone: isDineIn ? "" : orderForm.customer_phone.trim(),
                order_type: orderForm.order_type,
                table_number: isDineIn ? Number(orderForm.table_number) : null,
                delivery_address: orderForm.order_type === "delivery" ? orderForm.delivery_address.trim() : "",
                payment_method: orderForm.order_type === "delivery" ? orderForm.payment_method : null,
                notes: orderForm.notes,
            };
            const r = await api.post("/orders/public", body);
            setCart({});
            setCheckoutOpen(false);
            setSuccessOpen(r.data.order_no);
        } catch (e) {
            const d = e.response?.data?.detail;
            const msg = Array.isArray(d)
                ? d.map((x) => x?.msg || JSON.stringify(x)).join(" · ")
                : (typeof d === "string" ? d : t("error"));
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (!data) {
        return <div className="customer-menu flex items-center justify-center min-h-screen">{t("loading")}</div>;
    }

    const s = data.settings || {};
    const isAr = lang === "ar";
    const deliveryFee = Number(s.delivery_fee || 0);

    return (
        <div className="customer-menu menu-theme pb-24" dir={dir} data-testid="public-menu-page">
            <div className="relative">
                <div
                    className="h-56 md:h-72 w-full bg-cover bg-center relative"
                    style={{ backgroundImage: "linear-gradient(180deg, rgba(26,15,8,0.2) 0%, rgba(26,15,8,0.85) 100%), url('https://customer-assets.emergentagent.com/job_cafe24-pos/artifacts/7x6t55q8_FB_IMG_1775584277880.jpg')" }}
                >
                    <div className="absolute top-4 end-4">
                        <Button variant="secondary" size="sm" onClick={() => setLang(isAr ? "en" : "ar")} className="gap-2 backdrop-blur" data-testid="public-lang-toggle">
                            <Languages className="h-4 w-4" />{t("switch_lang")}
                        </Button>
                    </div>
                    <div className="absolute bottom-6 start-6 end-6 text-white">
                        <h1 className={`${isAr ? "font-ar" : "font-display-en italic"} text-4xl md:text-5xl drop-shadow-lg`}>{isAr ? s.cafe_name_ar : s.cafe_name_en}</h1>
                        {tableNo && (<div className="text-sm mt-1 opacity-90">{t("table")} #{tableNo}</div>)}
                    </div>
                </div>
            </div>

            <div className="sticky top-0 z-10 bg-[#fdfaf5]/95 backdrop-blur border-b border-[#e8d8c3]">
                <div className="flex gap-2 px-4 py-3 overflow-x-auto">
                    {data.categories.map((c) => (
                        <button key={c.id} data-testid={`menu-cat-${c.id}`} onClick={() => setActive(c.id)}
                            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap active:scale-95 transition-all ${active === c.id ? "bg-[#b48235] text-white" : "bg-[#f5ebd9] text-[#1a0f08] hover:bg-[#e8d8c3]"}`}>
                            {isAr ? c.name_ar : c.name_en}
                        </button>
                    ))}
                </div>
            </div>

            <div className="max-w-5xl mx-auto p-4 md:p-8">
                {itemsByCat.length === 0 ? (
                    <div className="text-center py-12 text-[#5c4839]"><Coffee className="h-10 w-10 mx-auto opacity-30 mb-2" />{t("no_menu_items")}</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {itemsByCat.map((m, idx) => {
                            const inCart = cart[m.id];
                            return (
                                <article key={m.id} data-testid={`menu-item-${m.id}`}
                                    className="rounded-2xl overflow-hidden bg-white border border-[#e8d8c3] shadow-sm fade-up group"
                                    style={{ animationDelay: `${idx * 30}ms` }}>
                                    {m.image_url ? (
                                        <div className="aspect-[4/3] overflow-hidden bg-[#f5ebd9]">
                                            <img src={m.image_url} alt={isAr ? m.name_ar : m.name_en} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                                        </div>
                                    ) : (
                                        <div className="aspect-[4/3] bg-[#f5ebd9] flex items-center justify-center"><Coffee className="h-12 w-12 text-[#b48235] opacity-40" /></div>
                                    )}
                                    <div className="p-4">
                                        <h3 className={`${isAr ? "font-ar text-lg" : "font-display-en text-xl"} font-semibold text-[#1a0f08] leading-tight`}>
                                            {isAr ? m.name_ar : m.name_en}
                                        </h3>
                                        <div className="flex items-center justify-between mt-3">
                                            <div className="text-[#b48235] font-medium text-lg tabular-nums">
                                                {m.price > 0 ? fmtSDG(m.price, lang) : "—"}
                                            </div>
                                            {m.price > 0 && (
                                                inCart ? (
                                                    <div className="flex items-center gap-2">
                                                        <Button size="icon" variant="outline" onClick={() => sub(m.id)} className="h-8 w-8 rounded-full border-[#b48235] text-[#b48235]" data-testid={`menu-minus-${m.id}`}>
                                                            <Minus className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <span className="tabular-nums font-medium text-[#1a0f08] w-5 text-center">{inCart.qty}</span>
                                                        <Button size="icon" onClick={() => add(m)} className="h-8 w-8 rounded-full bg-[#b48235] hover:bg-[#a07229]" data-testid={`menu-plus-${m.id}`}>
                                                            <Plus className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <Button size="sm" onClick={() => add(m)} className="bg-[#b48235] hover:bg-[#a07229] text-white rounded-full h-9 px-4" data-testid={`menu-add-${m.id}`}>
                                                        <Plus className="h-3.5 w-3.5 me-1" />{t("add")}
                                                    </Button>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>

            <footer className="border-t border-[#e8d8c3] bg-[#f5ebd9] mt-8 p-8 text-center">
                <div className="flex justify-center mb-4"><Logo size={56} theme="light" /></div>
                <div className="text-base font-medium text-[#1a0f08] mb-1">{t("show_your_waiter")}</div>
                <div className="text-xs text-[#5c4839]">{s.address || ""}</div>
            </footer>

            {/* Floating Cart Button */}
            {cartTotals.count > 0 && (
                <div className="fixed bottom-4 inset-x-4 z-30 flex justify-center" data-testid="floating-cart">
                    <Button
                        onClick={() => setCheckoutOpen(true)}
                        className="w-full max-w-md h-14 rounded-full shadow-xl bg-[#1a0f08] hover:bg-[#2a1a0e] text-[#fdfaf5] text-base"
                        data-testid="cart-checkout-btn"
                    >
                        <ShoppingBag className="h-5 w-5 me-3" />
                        <span className="me-2 bg-[#b48235] rounded-full w-6 h-6 flex items-center justify-center text-xs tabular-nums">{cartTotals.count}</span>
                        <span className="flex-1 text-start">{t("your_order")}</span>
                        <span className="tabular-nums">{fmtSDG(cartTotals.subtotal, lang)}</span>
                    </Button>
                </div>
            )}

            {/* Checkout Dialog */}
            <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
                <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="checkout-dialog">
                    <DialogHeader>
                        <DialogTitle>{t("your_order")}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Items */}
                        <div className="space-y-2 max-h-44 overflow-y-auto border border-border rounded-xl p-3 bg-secondary/30">
                            {Object.values(cart).map((x, i) => (
                                <div key={i} className="flex items-center justify-between text-sm" data-testid={`checkout-item-${i}`}>
                                    <div className="flex items-center gap-2 flex-1">
                                        <span className="tabular-nums text-muted-foreground">{x.qty}×</span>
                                        <span>{isAr ? x.item.name_ar : x.item.name_en}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="tabular-nums">{fmtSDG(x.item.price * x.qty, lang)}</span>
                                        <Button size="icon" variant="ghost" onClick={() => clearItem(x.item.id)} className="h-6 w-6"><X className="h-3 w-3" /></Button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Totals — directly under items */}
                        <div className="rounded-xl bg-primary/10 border border-primary/30 p-3 space-y-1.5">
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{t("subtotal")}</span>
                                <span className="tabular-nums">{fmtSDG(cartTotals.subtotal, lang)}</span>
                            </div>
                            {orderForm.order_type === "delivery" && deliveryFee > 0 && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{t("delivery_fee")}</span>
                                    <span className="tabular-nums">{fmtSDG(deliveryFee, lang)}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-baseline border-t border-primary/30 pt-1.5">
                                <span className="text-sm font-medium">{t("total")}</span>
                                <span className="text-primary text-2xl font-bold tabular-nums">{fmtSDG(cartTotals.subtotal + (orderForm.order_type === "delivery" ? deliveryFee : 0), lang)}</span>
                            </div>
                        </div>

                        {orderTypes.length === 0 ? (
                            <div className="rounded-xl bg-amber-500/10 border border-amber-500/40 px-4 py-6 text-center text-amber-200" data-testid="all-channels-disabled">
                                {isAr
                                    ? "الطلب عبر التطبيق متوقف حالياً. الرجاء التواصل مع الويتر."
                                    : "Online ordering is currently closed. Please ask your waiter."}
                            </div>
                        ) : (
                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <Label>{t("order_type")}</Label>
                                <div className={`grid gap-2 ${orderTypes.length === 1 ? "grid-cols-1" : orderTypes.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
                                    {orderTypes.map((opt) => (
                                        <button
                                            key={opt.key}
                                            type="button"
                                            onClick={() => setOrderForm({ ...orderForm, order_type: opt.key })}
                                            className={`px-3 py-2.5 rounded-xl text-sm border transition-all active:scale-95 ${orderForm.order_type === opt.key ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border hover:border-primary/40"}`}
                                            data-testid={`order-type-${opt.key}`}
                                        >
                                            {t(opt.key)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {orderForm.order_type === "dine_in" && (
                                <div className="space-y-1.5">
                                    <Label>{t("table_number")}</Label>
                                    <div className="flex items-start gap-2 rounded-xl bg-amber-500/10 border border-amber-500/40 px-3 py-2.5">
                                        <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                        <p className="text-[12px] leading-snug text-amber-200" data-testid="table-warning">
                                            {isAr
                                                ? "تنويه: لا تكتب أي رقم. أدخل رقم الطاولة الذي زوّدك به الويتر فقط."
                                                : "Notice: Do not enter a random number. Only use the table number provided to you by the waiter."}
                                        </p>
                                    </div>
                                    <Input
                                        type="number"
                                        min="1"
                                        max="70"
                                        inputMode="numeric"
                                        value={orderForm.table_number || ""}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            if (raw === "") {
                                                setOrderForm({ ...orderForm, table_number: null });
                                                return;
                                            }
                                            let n = Math.floor(Number(raw));
                                            if (isNaN(n)) return;
                                            if (n < 1) n = 1;
                                            if (n > 70) n = 70;
                                            setOrderForm({ ...orderForm, table_number: n });
                                        }}
                                        onBlur={(e) => {
                                            const n = Number(e.target.value);
                                            if (n && (n < 1 || n > 70)) {
                                                toast.error(isAr ? "رقم الطاولة من 1 إلى 70" : "Table number must be 1–70");
                                            }
                                        }}
                                        placeholder="1 – 70"
                                        className="text-xl h-14 text-center tabular-nums font-medium"
                                        data-testid="checkout-table"
                                    />
                                </div>
                            )}

                            {orderForm.order_type !== "dine_in" && (
                                <>
                                    <div className="space-y-1.5">
                                        <Label>{t("customer_name")}</Label>
                                        <Input value={orderForm.customer_name} onChange={(e) => setOrderForm({ ...orderForm, customer_name: e.target.value })} data-testid="checkout-name" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>{t("customer_phone")}</Label>
                                        <Input type="tel" value={orderForm.customer_phone} onChange={(e) => setOrderForm({ ...orderForm, customer_phone: e.target.value })} placeholder="09xxxxxxxx" data-testid="checkout-phone" />
                                    </div>
                                </>
                            )}

                            {orderForm.order_type === "delivery" && (
                                <>
                                    <div className="space-y-1.5">
                                        <Label>{t("delivery_address")}</Label>
                                        <Textarea rows={2} value={orderForm.delivery_address} onChange={(e) => setOrderForm({ ...orderForm, delivery_address: e.target.value })} placeholder={isAr ? "الحي، الشارع، علامة مميزة..." : "Neighborhood, street, landmark..."} data-testid="checkout-address" />
                                        {deliveryFee > 0 && (
                                            <div className="text-xs text-muted-foreground">{t("delivery_fee")}: <span className="tabular-nums text-primary">{fmtSDG(deliveryFee, lang)}</span></div>
                                        )}
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>{t("payment_method")}</Label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {[
                                                { key: "cash", label: t("cash") },
                                                { key: "bankak", label: t("bankak") },
                                            ].map((opt) => (
                                                <button
                                                    key={opt.key}
                                                    type="button"
                                                    onClick={() => setOrderForm({ ...orderForm, payment_method: opt.key })}
                                                    className={`px-3 py-2.5 rounded-xl text-sm border transition-all active:scale-95 ${orderForm.payment_method === opt.key ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border hover:border-primary/40"}`}
                                                    data-testid={`payment-method-${opt.key}`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="space-y-1.5">
                                <Label>{t("notes")}</Label>
                                <Textarea rows={2} value={orderForm.notes} onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })} />
                            </div>
                        </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCheckoutOpen(false)}>{t("cancel")}</Button>
                        <Button onClick={submitOrder} disabled={submitting || cartTotals.count === 0 || orderTypes.length === 0} className="flex-1 h-12" data-testid="place-order-btn">
                            {submitting ? t("loading") : t("place_order")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Success Dialog */}
            <Dialog open={!!successOpen} onOpenChange={(o) => !o && setSuccessOpen(null)}>
                <DialogContent data-testid="success-dialog">
                    <div className="py-6 text-center space-y-3">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-500">
                            <CheckCircle2 className="h-8 w-8" />
                        </div>
                        <h2 className={`${isAr ? "font-ar" : "font-display-en italic"} text-2xl`}>{t("order_placed")} #{successOpen}</h2>
                        <p className="text-sm text-muted-foreground">
                            {isAr ? "سيتم تأكيد طلبك من الكاشير قريباً." : "Your order will be confirmed by the cashier shortly."}
                        </p>
                        <Button onClick={() => setSuccessOpen(null)} className="mt-4 w-full">{t("back")}</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
